import React from "react";
import { Box, Typography, Link, useTheme } from "@mui/material";
import { getClientAssets } from "../../services/client-assets";
import { useLanguage } from "../../contexts/LanguageContext";
import { config } from "../../config/config";

interface PortalFooterProps {
  clientData: {
    subdomain: string;
    clientName: string;
  };
}

const PortalFooter: React.FC<PortalFooterProps> = ({ clientData }) => {
  const theme = useTheme();
  const { languageCode } = useLanguage();
  const { fallbackLogoUrl } = getClientAssets(clientData.subdomain);

  // Generate footer link URLs
  const getFooterLinkUrl = (page: string): string => {
    const baseUrl = config.forms.baseUrl.replace(/\/$/, "");
    return `${baseUrl}/${clientData.subdomain}/${page}.html?lang=${languageCode}`;
  };

  const linkStyle = {
    color: theme.palette.text.secondary,
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
      color: theme.palette.primary.main,
    },
  };

  return (
    <Box
      component="footer"
      sx={{
        mt: "auto", // Push footer to bottom
        py: 2,
        px: 3,
        bgcolor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {/* Left side - Client info and links */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="body2" color="text.secondary">
          © 2024 {clientData.clientName}
        </Typography>
        <Link
          href={getFooterLinkUrl("privacy-policy")}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={linkStyle}
        >
          Privacy Policy
        </Link>
        <Link
          href={getFooterLinkUrl("your-rights")}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={linkStyle}
        >
          Your Rights
        </Link>
        <Link
          href={getFooterLinkUrl("contact-dpo")}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={linkStyle}
        >
          Contact DPO
        </Link>
      </Box>

      {/* Right side - Powered by Privigo */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Powered by
        </Typography>
        <Box
          component="img"
          src={fallbackLogoUrl}
          alt="Privigo logo"
          sx={{
            height: "20px",
            width: "auto",
            objectFit: "contain",
          }}
        />
        <Link
          href="https://privigo.ai"
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            textDecoration: "none",
            "&:hover": {
              textDecoration: "underline",
              color: theme.palette.primary.main,
            },
          }}
        >
          Privigo
        </Link>
      </Box>
    </Box>
  );
};

export default PortalFooter;
